import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCMS } from '../context/CMSContext';
import { blogs as defaultBlogs } from '../data/mockData';

const BlogPostPage = () => {
  const { slug } = useParams();
  const { get } = useCMS();
  const posts = get('blogs') || defaultBlogs;
  const cmsPost = posts.find((p) => p.slug === slug);
  // Body content lives in the local data file; look it up by slug if CMS entry lacks it
  const localMatch = defaultBlogs.find((p) => p.slug === slug);
  const post = cmsPost ? { ...localMatch, ...cmsPost, body: cmsPost.body || localMatch?.body || cmsPost.excerpt } : null;

  if (!post) return <Navigate to="/blog" replace />;

  const related = posts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      <article className="relative pt-32 lg:pt-40 pb-16" data-testid="blog-post">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-10 transition-colors" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-6">
            <span className="px-3 py-1 rounded-full border border-white/15">{post.category}</span>
            <span>{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{post.readTime}</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-white leading-[1.02]">
            {post.title}
          </motion.h1>
          <p className="mt-6 text-white/60 text-lg font-light leading-relaxed">{post.excerpt}</p>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-8 mt-12">
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <div className="aspect-[16/8]">
              <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-16">
          <div className="prose prose-invert prose-lg max-w-none text-white/70 font-light leading-relaxed">
            {(post.body || post.excerpt).split(/\n\n+/).map((para, i) => (
              <p key={i} className="mb-6 text-base lg:text-lg">{para}</p>
            ))}
          </div>
          <div className="mt-14 border-t border-white/8 pt-10">
            <Link to="/contact"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#050914] font-semibold hover:scale-[1.03] transition-transform"
              data-testid="blog-post-cta">
              Book a consultation <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="relative pt-16 pb-24 border-t border-white/5" data-testid="blog-related">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-8">Related reading</p>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-white/45 font-bold mb-2">{r.category} · {r.readTime}</div>
                    <h4 className="font-display text-lg text-white leading-snug">{r.title}</h4>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-white/70 text-sm group-hover:text-white transition-colors">
                      Read <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
